import { useEffect, useRef } from 'react';

// Detect if running inside a Capacitor native shell.
// Returns false in browser/PWA, true in iOS/Android app.
const isNative = () =>
  typeof window !== 'undefined' &&
  !!(window.Capacitor?.isNativePlatform?.());

const API = process.env.REACT_APP_API_URL || 'http://localhost:3001';

/**
 * usePushNotifications
 *
 * Registers the device for FCM via @capacitor-firebase/messaging when running
 * natively, sends the FCM token to the Doorly backend, and sets up notification
 * tap routing. On iOS the plugin handles the APNs↔FCM handoff, so getToken()
 * returns a real FCM token on BOTH platforms (the raw APNs token the old
 * @capacitor/push-notifications flow sent was rejected by the FCM backend).
 *
 * Safe no-op in browser/PWA mode — never throws.
 *
 * @param {string|null} token - Doorly JWT (null = not logged in)
 */
export function usePushNotifications(token) {
  // Prevent double-registration across re-renders
  const registered = useRef(false);

  useEffect(() => {
    // Only run in native Capacitor shell + logged in
    if (!isNative()) return;
    if (!token) return;
    if (registered.current) return;

    let FirebaseMessaging;
    let listeners = [];

    // Send an FCM token to the Doorly backend (server-side upsert).
    const sendToken = async (deviceToken) => {
      if (!deviceToken) return;
      try {
        await fetch(`${API}/api/notifications/register-device`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            device_token: deviceToken,
            platform: window.Capacitor?.getPlatform?.() || 'unknown',
          }),
        });
        console.log('[Push] Token registered with Doorly');
      } catch (err) {
        console.error('[Push] Token registration failed:', err);
      }
    };

    const setup = async () => {
      try {
        const mod = await import('@capacitor-firebase/messaging');
        FirebaseMessaging = mod.FirebaseMessaging;

        // Request permission (shows the iOS system prompt)
        const perm = await FirebaseMessaging.requestPermissions();
        if (perm.receive !== 'granted') {
          console.log('[Push] Permission not granted');
          return;
        }

        // Android: create the notification channel the backend targets
        // (channelId 'doorly_default'). Without an EXISTING channel, Android 8+
        // silently drops incoming notifications even when FCM reports success.
        // No-op on iOS (createChannel is Android-only).
        if (window.Capacitor?.getPlatform?.() === 'android') {
          try {
            await FirebaseMessaging.createChannel({
              id: 'doorly_default',
              name: 'Doorly Notifications',
              description: 'Loan updates, messages, and reminders',
              importance: 5,   // HIGH — heads-up banner + sound
              visibility: 1,   // show full content on the lock screen
              sound: 'default',
              vibration: true,
              lights: true,
            });
          } catch (chErr) {
            console.warn('[Push] createChannel failed:', chErr.message);
          }
        }

        // Retrieve the FCM token and register it. getToken() triggers native
        // registration and REJECTS on failure (no separate registrationError
        // event), so wrap it in try/catch.
        //
        // ⚠️ THIS RE-FIRES ON EVERY LAUNCH FOR A LOGGED-IN USER, AND THAT IS
        // CORRECT — NOT AN OVERSIGHT TO "FIX". `registered` is a useRef, so it
        // resets with the process; each cold start re-fetches the token. FCM
        // tokens rotate (app restore, data clear, Google's own rotation), and
        // re-fetching on launch is how the backend's device_token stays current.
        // Registering once and trusting it forever is how push silently stops
        // working for a user with no visible symptom.
        //
        // ⚠️ FREQUENCY WAS NEVER THE BUG. The predecessor of this block called
        // @capacitor/push-notifications' register(), which reached
        // FirebaseMessaging.getInstance() SYNCHRONOUSLY on the native main
        // thread — so with Firebase uninitialised it threw
        // IllegalStateException past the bridge, uncatchable from JS, and
        // force-finished MainActivity. Because the whole block sits behind a
        // permission check that returns 'granted' with no prompt once the
        // Android 13+ grant exists, every subsequent launch crashed, and
        // clearing app data never helped — the grant is held by the system.
        // getToken() rejects into the catch below instead, which is the shape
        // that makes re-firing safe. Keep it that way.
        try {
          const { token: fcmToken } = await FirebaseMessaging.getToken();
          registered.current = true;
          await sendToken(fcmToken);
        } catch (tokErr) {
          console.error('[Push] getToken failed:', tokErr.message);
        }

        // Token refresh — re-register the rotated token.
        const onTokenReceived = (event) => {
          console.log('[Push] Token refreshed');
          sendToken(event.token);
        };

        // Foreground notification received
        const onForeground = (event) => {
          const notification = event.notification;
          console.log('[Push] Foreground notification:', notification?.title);
          window.dispatchEvent(new CustomEvent('doorly:push', { detail: notification }));
        };

        // Notification tapped — navigate to route
        const onTap = (event) => {
          const route = event.notification?.data?.route;
          if (route) {
            console.log('[Push] Navigating to:', route);
            window.location.href = route;
          }
        };

        // Add listeners and store references for cleanup
        listeners = await Promise.all([
          FirebaseMessaging.addListener('tokenReceived', onTokenReceived),
          FirebaseMessaging.addListener('notificationReceived', onForeground),
          FirebaseMessaging.addListener('notificationActionPerformed', onTap),
        ]);
      } catch (err) {
        // Never crash the app over push setup
        console.error('[Push] Setup failed:', err.message);
      }
    };

    setup();

    // Cleanup on unmount / token change
    return () => {
      if (FirebaseMessaging && listeners.length) {
        FirebaseMessaging.removeAllListeners().catch(() => {});
      }
      registered.current = false;
    };
  }, [token]);
}
